import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindText, html, list } from "../lib/bind";
import { formatDate } from "../lib/date";
import { formatMoney } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import {
  type VatPeriod,
  type VatReport,
  type VatReportLine,
  VAT_TREATMENT_LABELS,
  backfillVatPeriods,
  getVatReport,
  listVatPeriods,
  lockVatPeriod,
  unlockVatPeriod,
  vatReportCsvUrl,
  vatReportPackageUrl,
  vatReportPdfUrl,
} from "../services/vat";

function periodLabel(period: VatPeriod): string {
  return `${formatDate(period.start_date)} – ${formatDate(period.end_date)}`;
}

const TURNOVER_METHOD_LABELS: Record<string, string> = {
  cash: "Cash (payment date)",
  invoice: "Invoice (issue date)",
};

export function vatView(): Node {
  const periods = signal<VatPeriod[]>([]);
  const loadError = signal("");
  const selectedPeriodId = signal<string | null>(null);
  const report = signal<VatReport | null>(null);
  const reportError = signal("");
  const reportLoading = signal(false);

  function reloadPeriods(): void {
    listVatPeriods()
      .then((result) => {
        periods.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  reloadPeriods();

  const backfilling = signal(false);

  async function regeneratePeriods(): Promise<void> {
    backfilling.value = true;
    loadError.value = "";
    try {
      const result = await backfillVatPeriods();
      const skipped = result.skipped_closed_years;
      const parts = [
        result.created.length === 0
          ? "No missing periods to add."
          : `Added ${String(result.created.length)} period(s).`,
      ];
      if (skipped.length > 0) {
        const ranges = skipped
          .map((s) => `${formatDate(s.start_date)} – ${formatDate(s.end_date)}`)
          .join(", ");
        parts.push(
          `${String(skipped.length)} skipped because their accounting year is closed: ${ranges}.`,
        );
      }
      toast.success(parts.join(" "));
      reloadPeriods();
    } catch (err: unknown) {
      loadError.value =
        err instanceof ApiRequestError ? err.body.message : "Could not regenerate periods";
      toast.error(loadError.value);
    } finally {
      backfilling.value = false;
    }
  }

  function selectPeriod(periodId: string): void {
    selectedPeriodId.value = periodId;
    reportError.value = "";
    reportLoading.value = true;
    getVatReport(periodId)
      .then((result) => {
        report.value = result;
      })
      .catch((err: unknown) => {
        report.value = null;
        reportError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load report";
      })
      .finally(() => {
        reportLoading.value = false;
      });
  }

  async function doLock(): Promise<void> {
    const periodId = selectedPeriodId.value;
    if (!periodId) return;
    if (
      !window.confirm(
        "Lock this period? Figures will be snapshotted and the contributing invoices/payments " +
          "will no longer be editable.",
      )
    ) {
      return;
    }
    try {
      await lockVatPeriod(periodId);
      toast.success("Period locked.");
      reloadPeriods();
      selectPeriod(periodId);
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Lock failed");
    }
  }

  async function doUnlock(): Promise<void> {
    const periodId = selectedPeriodId.value;
    if (!periodId) return;
    if (!window.confirm("Unlock this period? This is audited and should only be used to correct a mistake.")) {
      return;
    }
    try {
      await unlockVatPeriod(periodId);
      toast.success("Period unlocked.");
      reloadPeriods();
      selectPeriod(periodId);
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Unlock failed");
    }
  }

  const isLocked = computed(() => report.value?.period.status === "locked");
  const hasReport = computed(() => report.value !== null && !reportLoading.value);
  // Shown once a period is picked, regardless of whether it loaded — a failed fetch
  // (e.g. no FRS rate set on the company) must still be visible, not silently no-op.
  const showReportCard = computed(() => selectedPeriodId.value !== null);
  const showReportContent = computed(() => hasReport.value && reportError.value === "");
  const flagsText = computed(() => {
    const flags = report.value?.flags;
    if (!flags) return "";
    const parts: string[] = [];
    if (flags.unpaid_invoices_excluded.length > 0) {
      parts.push(`Unpaid invoices excluded: ${flags.unpaid_invoices_excluded.join(", ")}`);
    }
    if (flags.non_gbp_invoice_numbers.length > 0) {
      parts.push(`Non-GBP invoices (converted): ${flags.non_gbp_invoice_numbers.join(", ")}`);
    }
    return parts.join(" · ");
  });

  const fragment = html`<div class="view">
    <p class="form-error"></p>

    <div class="card-label-row periods-header">
      <span class="card-label">VAT periods</span>
      <button class="btn regenerate-periods-btn" type="button" onclick="${() => void regeneratePeriods()}">
        Regenerate missing periods
      </button>
    </div>
    <p class="field-hint">
      Adds any quarter between your earliest invoice and today that isn't listed — needed
      after importing backdated invoices. Quarters inside a closed accounting year are left
      alone.
    </p>

    <div class="data-table">
      <table>
        <thead>
          <tr>
            <th>Period</th>
            <th>Status</th>
            <th>Box 1</th>
            <th>Box 6</th>
            <th></th>
          </tr>
        </thead>
        <tbody class="periods-body"></tbody>
      </table>
    </div>

    <div class="card report-card">
      <div class="page-header">
        <span class="card-label">Report</span>
        <div class="invoice-actions report-actions">
          <a class="btn download-csv-link" target="_blank" rel="noopener">Download CSV</a>
          <a class="btn download-pdf-link" target="_blank" rel="noopener">Download PDF</a>
          <a class="btn download-package-link">Download package</a>
          <button class="btn lock-btn" type="button">Lock period</button>
          <button class="btn btn-danger unlock-btn" type="button">Unlock period</button>
        </div>
      </div>
      <p class="form-error report-error"></p>

      <div class="report-content">
        <p class="report-period"></p>

        <div class="form-row">
          <div class="form-field">
            <span class="card-label">Turnover method</span>
            <span class="report-method"></span>
          </div>
          <div class="form-field">
            <span class="card-label">FRS rate applied</span>
            <span class="report-rate"></span>
          </div>
          <div class="form-field">
            <span class="card-label">Gross turnover (GBP)</span>
            <span class="report-turnover"></span>
          </div>
        </div>

        <table class="invoice-totals">
          <tr class="invoice-total-row"><td>Box 1 — VAT due</td><td class="report-box1"></td></tr>
          <tr class="invoice-total-row"><td>Box 6 — Turnover</td><td class="report-box6"></td></tr>
        </table>

        <p class="field-hint report-flags"></p>
        <p class="field-hint report-scheme-note"></p>

        <div class="data-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice</th>
                <th>Client</th>
                <th>Gross</th>
                <th>Gross (GBP)</th>
                <th>Treatment</th>
              </tr>
            </thead>
            <tbody class="report-lines-body"></tbody>
          </table>
        </div>

        <div class="report-excluded">
          <div class="card-label">Excluded from flat-rate turnover</div>
          <p class="field-hint">
            Supplies outside the scope of UK VAT — the flat rate doesn't apply to them.
            Zero-rated and exempt supplies are <strong>not</strong> here: those do count
            towards flat-rate turnover.
          </p>
          <div class="data-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Client</th>
                  <th>Gross</th>
                  <th>Gross (GBP)</th>
                  <th>Treatment</th>
                </tr>
              </thead>
              <tbody class="report-excluded-body"></tbody>
            </table>
          </div>
          <table class="invoice-totals">
            <tr class="invoice-total-row">
              <td>Total excluded</td><td class="report-excluded-total"></td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const periodsTable = fragment.querySelector<HTMLTableElement>(".data-table table");
  const periodsBody = fragment.querySelector<HTMLTableSectionElement>(".periods-body");
  const regenerateBtn = fragment.querySelector<HTMLButtonElement>(".regenerate-periods-btn");
  const reportCard = fragment.querySelector<HTMLElement>(".report-card");
  const reportActions = fragment.querySelector<HTMLElement>(".report-actions");
  const reportContent = fragment.querySelector<HTMLElement>(".report-content");
  const reportErrorEl = fragment.querySelector<HTMLParagraphElement>(".report-error");
  const methodEl = fragment.querySelector<HTMLElement>(".report-method");
  const rateEl = fragment.querySelector<HTMLElement>(".report-rate");
  const turnoverEl = fragment.querySelector<HTMLElement>(".report-turnover");
  const reportPeriodEl = fragment.querySelector<HTMLElement>(".report-period");
  const box1El = fragment.querySelector<HTMLElement>(".report-box1");
  const box6El = fragment.querySelector<HTMLElement>(".report-box6");
  const flagsEl = fragment.querySelector<HTMLElement>(".report-flags");
  const schemeNoteEl = fragment.querySelector<HTMLElement>(".report-scheme-note");
  const linesTable = fragment.querySelectorAll<HTMLTableElement>(".data-table table")[1];
  const linesBody = fragment.querySelector<HTMLTableSectionElement>(".report-lines-body");
  const excludedSection = fragment.querySelector<HTMLElement>(".report-excluded");
  const excludedTable = fragment.querySelectorAll<HTMLTableElement>(".data-table table")[2];
  const excludedBody = fragment.querySelector<HTMLTableSectionElement>(".report-excluded-body");
  const excludedTotalEl = fragment.querySelector<HTMLElement>(".report-excluded-total");
  const csvLink = fragment.querySelector<HTMLAnchorElement>(".download-csv-link");
  const pdfLink = fragment.querySelector<HTMLAnchorElement>(".download-pdf-link");
  const packageLink = fragment.querySelector<HTMLAnchorElement>(".download-package-link");
  const lockBtn = fragment.querySelector<HTMLButtonElement>(".lock-btn");
  const unlockBtn = fragment.querySelector<HTMLButtonElement>(".unlock-btn");

  if (
    !errorEl ||
    !periodsTable ||
    !periodsBody ||
    !regenerateBtn ||
    !reportCard ||
    !reportActions ||
    !reportContent ||
    !reportErrorEl ||
    !methodEl ||
    !rateEl ||
    !turnoverEl ||
    !reportPeriodEl ||
    !box1El ||
    !box6El ||
    !flagsEl ||
    !schemeNoteEl ||
    !linesTable ||
    !linesBody ||
    !excludedSection ||
    !excludedTable ||
    !excludedBody ||
    !excludedTotalEl ||
    !csvLink ||
    !pdfLink ||
    !packageLink ||
    !lockBtn ||
    !unlockBtn
  ) {
    throw new Error("vat view markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);
  // Disabled in flight: the action is idempotent, but a second click while the first is
  // still open would report "no missing periods" and look like it had failed.
  currentScope().effect(() => {
    regenerateBtn.disabled = backfilling.value;
  });
  bindErrorText(reportErrorEl, reportError);
  bindShow(reportCard, showReportCard);
  bindShow(reportActions, showReportContent);
  bindShow(reportContent, showReportContent);
  bindShow(lockBtn, computed(() => showReportContent.value && !isLocked.value));
  bindShow(unlockBtn, computed(() => showReportContent.value && isLocked.value));

  currentScope().effect(() => {
    const r = report.value;
    if (!r) return;
    methodEl.textContent = TURNOVER_METHOD_LABELS[r.turnover_method] ?? r.turnover_method;
    rateEl.textContent = r.frs_rate_applied
      ? `${r.frs_rate_applied}%${r.first_year_discount_applied ? " (incl. 1% first-year discount)" : ""}`
      : "—";
    reportPeriodEl.textContent = periodLabel(r.period);
    schemeNoteEl.textContent = r.scheme_suitability_note ?? "";
    schemeNoteEl.style.display = r.scheme_suitability_note ? "" : "none";
    turnoverEl.textContent = formatMoney(r.gross_turnover_gbp);
    box1El.textContent = formatMoney(r.box1_vat_due);
    box6El.textContent = formatMoney(r.box6_turnover);
  });
  bindText(flagsEl, flagsText);

  currentScope().effect(() => {
    const periodId = selectedPeriodId.value;
    csvLink.href = periodId ? vatReportCsvUrl(periodId) : "#";
    pdfLink.href = periodId ? vatReportPdfUrl(periodId) : "#";
    packageLink.href = periodId ? vatReportPackageUrl(periodId) : "#";
  });

  lockBtn.addEventListener("click", () => {
    void doLock();
  });
  unlockBtn.addEventListener("click", () => {
    void doUnlock();
  });

  list(
    periodsBody,
    periods,
    (p) => p.id,
    (p) => {
      const row = html`<tr>
        <td>${periodLabel(p)}</td>
        <td><span class="badge ${p.status === "locked" ? "badge-ok" : "badge-neutral"}">${
          p.status === "locked" ? "Locked" : "Open"
        }</span></td>
        <td>${p.box1_vat_due ? formatMoney(p.box1_vat_due) : "—"}</td>
        <td>${p.box6_turnover ? formatMoney(p.box6_turnover) : "—"}</td>
        <td><button class="btn view-report-btn" type="button">View report</button></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("vat period row template produced no element");
      const viewBtn = row.querySelector<HTMLButtonElement>(".view-report-btn");
      viewBtn?.addEventListener("click", () => selectPeriod(p.id));
      return labelRow(row, periodsTable);
    },
  );

  function renderLine(l: VatReportLine, table: HTMLTableElement): Node {
    const row = html`<tr>
      <td>${formatDate(l.date_counted)}</td>
      <td>${l.invoice_number ?? "—"}</td>
      <td>${l.client_name}</td>
      <td>${formatMoney(l.gross_amount, l.currency)}</td>
      <td>${formatMoney(l.gross_amount_gbp)}</td>
      <td>${VAT_TREATMENT_LABELS[l.vat_treatment]}</td>
    </tr>`.firstElementChild;
    if (!row) throw new Error("vat report line row template produced no element");
    return labelRow(row, table);
  }

  const lineKey = (l: VatReportLine): string =>
    `${l.kind}-${l.invoice_id}-${l.date_counted}`;

  list(
    linesBody,
    computed(() => report.value?.lines ?? []),
    lineKey,
    (l) => renderLine(l, linesTable),
  );

  // Hidden entirely when nothing was excluded — an empty "excluded" table on the great
  // majority of periods would be noise, and its absence is itself the useful signal.
  bindShow(
    excludedSection,
    computed(() => (report.value?.excluded_lines.length ?? 0) > 0),
  );
  currentScope().effect(() => {
    const r = report.value;
    excludedTotalEl.textContent = r ? formatMoney(r.excluded_turnover_gbp) : "";
  });
  list(
    excludedBody,
    computed(() => report.value?.excluded_lines ?? []),
    lineKey,
    (l) => renderLine(l, excludedTable),
  );

  return fragment;
}
